import { useTranslation } from 'next-i18next';
import { JellyIcon } from '@/icons';
import { cn } from '@/utils/tailwind.util';

/**
 * 색 배분: 골드(#F5A623)는 코인과 그 글로우·문구 하이라이트에만,
 * 주변 광채(스윕·반짝이)는 색을 빼고 흰 빛으로 둔다.
 *
 * 전부 금색으로 두면 토스트가 주황색으로 물들어 탁해진다. 따뜻한 초점(코인)과
 * 무채색 광채(주변)로 나눠야 대비가 산다.
 * (Tailwind 임의값은 빌드 타임 문자열이라 색을 상수로 빼지 못해 클래스에 직접 적는다)
 */

/**
 * 4각 반짝임. 디자인 시스템의 `SparklingFillIcon` 은 큰 반짝임 1개 + 작은 것 2개가
 * 뭉친 클러스터라 14~16px 로 줄이면 형태가 안 읽히고 얼룩처럼 보인다.
 * 작은 크기에서도 또렷한 단일 4각 별을 따로 그린다(장식 전용).
 *
 * 색도 그림자도 넣지 않는다 — 흰 빛에 어두운 그림자를 붙이면 때가 탄 것처럼 보인다.
 * 대신 흰 halo 를 둘러 '빛나는 입자' 로 읽히게 한다. 흰 배경에서는 자연히 옅게 보이지만,
 * 실제 토스트는 앱 콘텐츠 위에 반투명(bg-opacity-white-10 + backdrop-blur)으로 얹히므로
 * 그 위에서는 또렷하다.
 */
function SparkleGlyph({
  size,
  className,
  style
}: {
  size: number;
  className?: string;
  style?: React.CSSProperties;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" className={cn('drop-shadow-[0_0_3px_rgba(255,255,255,0.95)]', className)} style={style} data-sentry-element="svg" data-sentry-component="SparkleGlyph" data-sentry-source-file="PointReceiveToast.tsx">
      <path d="M12 1c.6 6.2 4.8 10.4 11 11-6.2.6-10.4 4.8-11 11-.6-6.2-4.8-10.4-11-11 6.2-.6 10.4-4.8 11-11Z" fill="#FFFFFF" data-sentry-element="path" data-sentry-source-file="PointReceiveToast.tsx" />
    </svg>;
}

/**
 * 반짝임 입자 배치.
 *
 * 개수가 적고 크기·수명·방향이 같으면 '별을 몇 개 붙여둔' 것처럼 어색해진다.
 * 자연스럽게 보이려면 셋 다 흐트러져야 한다:
 * - 크기 6~12px, 수명 760~1020ms, 지연 640~1140ms 를 전부 다르게
 * - `x`/`y` 로 입자마다 흩어지는 방향을 다르게(코인 쪽은 안쪽, 가장자리는 바깥쪽)
 * - 코인 근처는 작고 촘촘하게, 가장자리는 크고 늦게 → 코인에서 튄 것처럼 읽힌다
 *
 * 문구가 토스트 우측 끝까지 차서 글자 위를 피해 배치한다. 토스트 본체에는
 * overflow-hidden 이 없어(빛 띠는 자체 래퍼 안에서 잘린다) 밖으로 나가도 잘리지 않는다.
 */
const SPARKLES = [
// 코인 주변 — 회전이 끝나는 순간 튀어나오는 것들. 작고 촘촘하다.
{
  className: 'left-[8px] top-[10px]',
  size: 7,
  x: -7,
  y: -9,
  delay: 640,
  duration: 760
}, {
  className: 'left-[34px] top-[3px]',
  size: 10,
  x: 5,
  y: -11,
  delay: 700,
  duration: 900
}, {
  className: 'left-[6px] top-[42px]',
  size: 6,
  x: -9,
  y: 7,
  delay: 830,
  duration: 820
}, {
  className: '-left-[7px] -top-[8px]',
  size: 9,
  x: -10,
  y: -8,
  delay: 770,
  duration: 950
},
// 토스트 가장자리 — 조금 늦게, 더 크게, 더 멀리 흩어진다.
{
  className: 'right-[44px] -top-[7px]',
  size: 12,
  x: 4,
  y: -12,
  delay: 900,
  duration: 1020
}, {
  className: '-right-[6px] top-[12px]',
  size: 8,
  x: 11,
  y: -5,
  delay: 1010,
  duration: 880
}, {
  className: 'right-[16px] -bottom-[7px]',
  size: 7,
  x: 6,
  y: 9,
  delay: 1140,
  duration: 840
}] as const;

/**
 * 학생이 포인트를 받았을 때 뜨는 토스트.
 *
 * 공용 `SuccessToast` 와 표면(글래스 + shadow-lg)은 같게 두고 두 가지만 다르다:
 * - 체크 대신 코인 아이콘 — 포인트 이벤트라 나머지 포인트 UI 와 글리프를 맞춘다.
 * - 칭찬 내용을 둘째 줄로 — "칭찬 내용이 하나도 안보여요" 제보가 3건이었고,
 *   SSE payload 의 `reasonDetail` 이 이미 오고 있어 표시만 하면 된다.
 *
 * 폭을 제한해 칭찬 내용이 길어도 화면을 가로지르지 않게 한다(플레이 중 노출 대비).
 */
export function PointReceiveToast({
  amount,
  reason,
  /** react-hot-toast 의 `t.visible`. false 로 바뀌면 퇴장 연출을 태운다. */
  isVisible = true
}: {
  amount: number;
  reason?: string;
  isVisible?: boolean;
}) {
  const {
    t
  } = useTranslation('classroom');
  return <div className={cn('relative isolate flex min-h-[48px] max-w-[320px] items-center gap-xs rounded-sm bg-opacity-white-10 p-sm shadow-lg backdrop-blur-[2px]', isVisible ? 'animate-point-toast-in' : 'animate-point-toast-out',
  // DESIGN.md: reduced-motion 이면 장식성 연출을 즉시로 접는다.
  'motion-reduce:animate-none')} data-sentry-component="PointReceiveToast" data-sentry-source-file="PointReceiveToast.tsx">
      {/*
        표면을 가로지르는 빛 띠. 자체 overflow-hidden 래퍼 안에 둬서 모서리 밖으로
        새지 않게 한다 — 토스트 본체에 overflow-hidden 을 주면 아래 반짝임 입자가 잘린다.
       */}
      <span aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-sm">
        {/*
          흰 빛 띠. 색(금·보라)을 넣으면 표면이 물들어 탁해지고, 어두운 테두리를 깔면
          때가 탄 것처럼 보여서 순수한 흰빛만 쓴다. 반투명 유리 위에 얹히므로
          앱 콘텐츠 위에서는 빛이 스치는 것으로 읽힌다.
         */}
        <span className="absolute inset-0 animate-point-toast-sweep bg-[linear-gradient(105deg,transparent_34%,rgba(255,255,255,0.45)_44%,rgba(255,255,255,0.95)_50%,rgba(255,255,255,0.45)_56%,transparent_66%)] bg-[length:200%_100%] bg-no-repeat motion-reduce:hidden" />
      </span>

      {/*
        반짝임 입자. 지연을 달리해 동시에 켜지지 않고 차례로 깜빡인다.
        장식이라 aria-hidden, 레이아웃에 영향 없도록 absolute.
       */}
      {SPARKLES.map(sparkle => <SparkleGlyph key={sparkle.className} size={sparkle.size} className={cn('pointer-events-none absolute animate-point-sparkle-twinkle motion-reduce:hidden', sparkle.className)} style={{
      animationDelay: `${sparkle.delay}ms`,
      // 수명도 입자마다 달라야 한 박자로 꺼지지 않는다.
      animationDuration: `${sparkle.duration}ms`,
      '--sparkle-x': `${sparkle.x}px`,
      '--sparkle-y': `${sparkle.y}px`
    } as React.CSSProperties} />)}

      <span className="relative flex size-[24px] shrink-0 items-center justify-center">
        {/*
          코인 뒤 금빛 글로우. DOM 상 코인보다 앞에 둬서 코인이 위에 그려지게 한다
          (둘 다 positioned 라 z-index 없이 순서로 정해진다).
          색은 포인트 지표에 쓰는 골드(#F5A623)와 맞춘다.
         */}
        <span aria-hidden="true" className="pointer-events-none absolute inset-0 animate-point-coin-glow rounded-full bg-[radial-gradient(circle,rgba(245,166,35,0.6)_0%,rgba(245,166,35,0.25)_45%,transparent_70%)] motion-reduce:animate-none" />
        <JellyIcon size="md" className="relative animate-point-coin-celebrate motion-reduce:animate-none" data-sentry-element="JellyIcon" data-sentry-source-file="PointReceiveToast.tsx" />
      </span>
      <div className="relative flex min-w-0 flex-col gap-4xs">
        {/*
          문구 위로 금빛 띠가 한 번 스친다(표면에 흰 광띠를 흘리는 .hover-shine 은
          이 흰 글래스 표면에선 묻혀서 글자에 clip 하는 방식으로 바꿨다).
          코어를 흰색으로 두면 흰 배경 위에서 글자가 순간 사라져 읽기 어렵다 —
          코인과 같은 골드(#F5A623)로 둬서 스치는 동안에도 대비를 유지한다.
          reduced-motion 이면 애니메이션과 그라디언트를 끄고 기본 텍스트색으로 남긴다.
         */}
        <span className="animate-point-amount-shine bg-[linear-gradient(105deg,theme(colors.gray.900)_38%,#F5A623_50%,theme(colors.gray.900)_62%)] bg-[length:200%_100%] bg-clip-text text-body-lg-semibold text-transparent motion-reduce:animate-none motion-reduce:bg-none motion-reduce:text-default">
          {t('pointReceiveToast.message', {
          amount
        })}
        </span>
        {reason && <span className="truncate text-body-sm-medium text-neutral">
            {reason}
          </span>}
      </div>
    </div>;
}